import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  X,
  Users,
  Briefcase,
  Wrench,
  CreditCard,
  MessageSquare,
  AlertTriangle,
  ChevronRight,
  ArrowUpRight,
  ShieldCheck,
} from 'lucide-react';
import { performAdminGlobalSearch } from '../../lib/supabaseService';

interface AdminGlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEntity: (type: string, id: string, entity?: any) => void;
}

export const AdminGlobalSearchModal: React.FC<AdminGlobalSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectEntity,
}) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<{
    customers: any[];
    professionals: any[];
    jobs: any[];
    payments: any[];
    whatsapp: any[];
    disputes: any[];
  }>({
    customers: [],
    professionals: [],
    jobs: [],
    payments: [],
    whatsapp: [],
    disputes: [],
  });

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResults({
        customers: [],
        professionals: [],
        jobs: [],
        payments: [],
        whatsapp: [],
        disputes: [],
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
      if (e.key === '/' && !isOpen && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        // Triggered from outside if needed
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!query.trim()) {
      setResults({
        customers: [],
        professionals: [],
        jobs: [],
        payments: [],
        whatsapp: [],
        disputes: [],
      });
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await performAdminGlobalSearch(query.trim());
        if (res && res.results) {
          setResults(res.results);
        }
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  if (!isOpen) return null;

  const totalResultsCount =
    results.customers.length +
    results.professionals.length +
    results.jobs.length +
    results.payments.length +
    results.disputes.length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
        {/* Search Input Bar */}
        <div className="p-4 border-b border-slate-800 flex items-center gap-3 bg-slate-900/90">
          <Search className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search artisans, customers, jobs, payments (ref/amount), disputes, or phone..."
            className="w-full bg-transparent text-white placeholder-slate-500 text-sm focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-200 text-xs"
            >
              Clear
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results Container */}
        <div className="overflow-y-auto p-4 space-y-4 flex-1">
          {loading && (
            <div className="py-12 text-center text-slate-400 text-xs flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span>Searching records...</span>
            </div>
          )}

          {!loading && !query && (
            <div className="py-12 text-center text-slate-500 text-xs space-y-2">
              <p>Type keywords to search across the entire JobMatch NG system.</p>
              <div className="flex justify-center gap-2 flex-wrap text-[11px] text-slate-400 pt-2">
                <span className="bg-slate-800 px-2 py-1 rounded">"Plumber"</span>
                <span className="bg-slate-800 px-2 py-1 rounded">"pstk_"</span>
                <span className="bg-slate-800 px-2 py-1 rounded">"Ikeja"</span>
                <span className="bg-slate-800 px-2 py-1 rounded">"Dispute"</span>
              </div>
            </div>
          )}

          {!loading && query && totalResultsCount === 0 && (
            <div className="py-12 text-center text-slate-400 text-xs space-y-1">
              <p className="font-semibold text-slate-300">No records found matching "{query}"</p>
              <p className="text-slate-400">Try searching by name, email, phone number, job title, or transaction reference.</p>
            </div>
          )}

          {/* Professionals Results */}
          {!loading && results.professionals.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-bold tracking-wider uppercase text-emerald-400 flex items-center gap-1.5 px-2">
                <Wrench className="w-3 h-3" />
                <span>Artisans & Professionals ({results.professionals.length})</span>
              </div>
              <div className="space-y-1">
                {results.professionals.map((pro) => (
                  <div
                    key={pro.id}
                    onClick={() => {
                      onSelectEntity('professionals', pro.id, pro);
                      onClose();
                    }}
                    className="p-3 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 hover:border-emerald-500/50 rounded-xl cursor-pointer transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-bold text-xs">
                        {pro.full_name?.charAt(0) || 'P'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white group-hover:text-emerald-300 transition-colors">
                            {pro.full_name}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-700 text-slate-300 font-medium">
                            {pro.primary_category || 'Artisan'}
                          </span>
                          {pro.verification_status === 'verified' && (
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                          <span>{pro.business_name || 'Artisan Service'}</span>
                          <span>•</span>
                          <span>{pro.state || 'Lagos'}</span>
                          <span>•</span>
                          <span className="font-mono">{pro.phone}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Customers Results */}
          {!loading && results.customers.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-bold tracking-wider uppercase text-blue-400 flex items-center gap-1.5 px-2">
                <Users className="w-3 h-3" />
                <span>Customers ({results.customers.length})</span>
              </div>
              <div className="space-y-1">
                {results.customers.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => {
                      onSelectEntity('customers', c.id, c);
                      onClose();
                    }}
                    className="p-3 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 hover:border-blue-500/50 rounded-xl cursor-pointer transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-300 flex items-center justify-center font-bold text-xs">
                        {c.full_name?.charAt(0) || 'C'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white group-hover:text-blue-300 transition-colors">
                            {c.full_name}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.2 rounded bg-blue-500/20 text-blue-300 font-medium">
                            Customer
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                          <span>{c.email}</span>
                          <span>•</span>
                          <span>{c.state || 'Nigeria'}</span>
                          <span>•</span>
                          <span className="font-mono">{c.phone}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Jobs Results */}
          {!loading && results.jobs.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-bold tracking-wider uppercase text-amber-400 flex items-center gap-1.5 px-2">
                <Briefcase className="w-3 h-3" />
                <span>Jobs & Projects ({results.jobs.length})</span>
              </div>
              <div className="space-y-1">
                {results.jobs.map((job) => (
                  <div
                    key={job.id}
                    onClick={() => {
                      onSelectEntity('jobs', job.id, job);
                      onClose();
                    }}
                    className="p-3 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 hover:border-amber-500/50 rounded-xl cursor-pointer transition-all flex items-center justify-between group"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-white group-hover:text-amber-300 transition-colors">
                          {job.title}
                        </span>
                        <span
                          className={`text-[10px] px-1.5 py-0.2 rounded font-semibold uppercase ${
                            job.status === 'open'
                              ? 'bg-emerald-500/20 text-emerald-300'
                              : job.status === 'assigned'
                              ? 'bg-blue-500/20 text-blue-300'
                              : 'bg-slate-700 text-slate-300'
                          }`}
                        >
                          {job.status}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5 line-clamp-1">
                        <span>{job.category_slug || 'General'}</span>
                        <span>•</span>
                        <span>{job.lga ? `${job.lga}, ${job.state}` : job.state || 'Lagos'}</span>
                        <span>•</span>
                        <span>ID: {job.id}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payments Results */}
          {!loading && results.payments.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-bold tracking-wider uppercase text-purple-400 flex items-center gap-1.5 px-2">
                <CreditCard className="w-3 h-3" />
                <span>Payments & Paystack Transactions ({results.payments.length})</span>
              </div>
              <div className="space-y-1">
                {results.payments.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => {
                      onSelectEntity('subscriptions', p.id, p);
                      onClose();
                    }}
                    className="p-3 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 hover:border-purple-500/50 rounded-xl cursor-pointer transition-all flex items-center justify-between group"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-white font-mono">
                          ₦{(Number(p.amount) || 0).toLocaleString()}
                        </span>
                        <span
                          className={`text-[10px] px-1.5 py-0.2 rounded font-semibold uppercase ${
                            p.payment_status === 'success'
                              ? 'bg-emerald-500/20 text-emerald-300'
                              : 'bg-rose-500/20 text-rose-300'
                          }`}
                        >
                          {p.payment_status}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5 font-mono">
                        <span>Ref: {p.payment_reference}</span>
                        <span>•</span>
                        <span>Pro ID: {p.professional_id}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-purple-400 group-hover:translate-x-0.5 transition-all" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Disputes Results */}
          {!loading && results.disputes.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-bold tracking-wider uppercase text-rose-400 flex items-center gap-1.5 px-2">
                <AlertTriangle className="w-3 h-3" />
                <span>Disputes ({results.disputes.length})</span>
              </div>
              <div className="space-y-1">
                {results.disputes.map((d) => (
                  <div
                    key={d.id}
                    onClick={() => {
                      onSelectEntity('disputes', d.id, d);
                      onClose();
                    }}
                    className="p-3 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 hover:border-rose-500/50 rounded-xl cursor-pointer transition-all flex items-center justify-between group"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-white group-hover:text-rose-300 transition-colors">
                          {d.reason || 'Dispute Case'}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-rose-500/20 text-rose-300 font-semibold uppercase">
                          {d.status}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                        <span>Job: {d.jobTitle || d.jobId}</span>
                        <span>•</span>
                        <span>Raised by: {d.raisedByName || d.raisedBy}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-rose-400 group-hover:translate-x-0.5 transition-all" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="p-3 bg-slate-950/80 border-t border-slate-800/80 text-[11px] text-slate-500 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span>Press <kbd className="bg-slate-800 px-1 rounded text-slate-300 border border-slate-700">ESC</kbd> to exit</span>
          </div>
          <span>JobMatch NG Database Indexed</span>
        </div>
      </div>
    </div>
  );
};
